// src/components/suiviEnseignants/ProgrammesEnseignant.jsx
// ============================================================================
// Page d'un enseignant — onglet « Programme »
// ============================================================================
//
// Un bloc par couple classe × matière qu'il enseigne cette année : où en est
// le programme (chapitres, élément en cours, devoirs), et les commentaires de
// la direction. Lecture seule : c'est l'enseignant qui tient son programme
// dans son espace (lot C). La direction ne valide plus rien ; elle commente,
// et l'enseignant lit ses commentaires sous l'en-tête du programme.
//
// « En retard », « terminé », l'élément en cours : décidés par le serveur, en
// SQL, avec les règles du lot C (ProgrammeEnseignantService). Rien ici ne
// compare une date.
// ============================================================================

import React, { useEffect, useId, useRef, useState } from 'react';
import { BookOpen, Loader2, MessageSquarePlus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import ConfirmDialog from '../ConfirmDialog';
import EtatVide from '../ui/EtatVide';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { BarreAvancement } from './elementsSuivi';
import { dateCourte, dateHeure, enPourcentage, estVrai, libelleDe, nombre, pluriel } from './formatSuivi';

const TEXTE_MAX = 2000;
// Boutons de pied de même largeur (DESIGN-SYSTEM.md) : à 168 px, « Publier le
// commentaire » et son icône débordaient (173 px mesurés) et le bouton
// dépassait « Annuler ».
const BOUTON_PIED = 'h-9 min-w-[188px] justify-center gap-2';

// Les types de devoirs du lot C (programme_evaluations.type).
const TYPES_DEVOIR = {
  interrogation: 'Interrogation',
  devoir_maison: 'Devoir maison',
  devoir_surveille: 'Devoir surveillé',
  expose: 'Exposé',
  examen: 'Examen',
  autre: 'Évaluation'
};

// Teintes des chapitres, celles de la maquette : vert terminé, bleu en cours,
// ambre en retard, gris prévu.
const TEINTES_CHAPITRE = {
  termine: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  en_cours: 'border-[#246990] bg-[#eef7fb] text-[#246990]',
  retard: 'border-amber-400 bg-amber-50 text-amber-800',
  prevu: 'border-gray-200 bg-gray-50 text-gray-600'
};

const titreProgramme = (p) => [libelleDe(p.classe), libelleDe(p.matiere)].filter(Boolean).join(' · ') || 'Programme';

function detailChapitre(ch) {
  const total = nombre(ch.elements);
  const faits = nombre(ch.elements_faits);
  const fin = ch.periode_fin ? `fin prévue ${dateCourte(ch.periode_fin)}` : 'sans date de fin';
  if (ch.statut === 'termine') return total ? `Terminé · ${pluriel(total, 'élément')}` : 'Terminé';
  if (ch.statut === 'en_cours' || estVrai(ch.en_retard)) return `${faits}/${total} · ${fin}`;
  return ch.periode_fin ? `Fin prévue ${dateCourte(ch.periode_fin)}` : 'Sans date de fin';
}

function teinteChapitre(ch) {
  if (ch.statut === 'termine') return TEINTES_CHAPITRE.termine;
  if (estVrai(ch.en_retard)) return TEINTES_CHAPITRE.retard;
  if (ch.statut === 'en_cours') return TEINTES_CHAPITRE.en_cours;
  return TEINTES_CHAPITRE.prevu;
}

/** « Interrogation faite le 10 nov. », « Devoir maison prévu le 24 nov. », « Exposé annulé ». */
function phraseDevoir(d) {
  const type = TYPES_DEVOIR[d.type] || 'Évaluation';
  // « Interrogation » et « Évaluation » sont féminins : « faite », « prévue ».
  const feminin = d.type === 'interrogation' || !TYPES_DEVOIR[d.type] || d.type === 'autre' ? 'e' : '';
  const titre = d.titre ? ` « ${d.titre} »` : '';
  if (d.statut === 'annule') return `${type}${titre} annulé${feminin}`;
  if (d.statut === 'fait') {
    return d.date_faite ? `${type}${titre} fait${feminin} le ${dateCourte(d.date_faite)}` : `${type}${titre} fait${feminin}`;
  }
  return d.date_prevue ? `${type}${titre} prévu${feminin} le ${dateCourte(d.date_prevue)}` : `${type}${titre} prévu${feminin}`;
}

/** La fenêtre de saisie d'un commentaire de la direction. */
const DialogCommentaire = ({ programme, onFermer, onEnvoyer }) => {
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Verrou immédiat : `envoi` ne désactive le bouton qu'au rendu suivant, et
  // une seconde soumission arrivée avant lui publierait le commentaire deux
  // fois (le serveur n'a pas de garde contre les doublons).
  const envoiEnCours = useRef(false);
  const idTexte = useId();
  const idAide = useId();

  useEffect(() => {
    setTexte('');
    setErreur(null);
  }, [programme?.programme_id]);

  const propre = texte.trim();

  const soumettre = async (e) => {
    e.preventDefault();
    if (!propre || propre.length > TEXTE_MAX || envoiEnCours.current) return;
    envoiEnCours.current = true;
    setEnvoi(true);
    setErreur(null);
    try {
      await onEnvoyer(programme, propre);
    } catch (err) {
      setErreur(err.message);
    } finally {
      envoiEnCours.current = false;
      setEnvoi(false);
    }
  };

  return (
    <Dialog open={Boolean(programme)} onOpenChange={(ouvert) => { if (!ouvert && !envoi) onFermer(); }}>
      <DialogContent
        className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <form onSubmit={soumettre} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <MessageSquarePlus className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Commenter le programme</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">{programme ? titreProgramme(programme) : ''}</DialogDescription>
            </div>
          </DialogHeader>
          <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {erreur && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</p>}
            <div>
              <label htmlFor={idTexte} className="mb-1 block text-sm text-gray-700">Commentaire</label>
              <textarea
                id={idTexte}
                rows={6}
                maxLength={TEXTE_MAX}
                required
                aria-describedby={idAide}
                className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
              />
              <p id={idAide} className="mt-1 flex justify-between gap-3 text-[11px] text-gray-500">
                <span>L'enseignant le lit dans son espace, sous l'en-tête de ce programme. Il n'y répond pas.</span>
                <span className="shrink-0 tabular-nums">{texte.length} / {TEXTE_MAX}</span>
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
            <Button type="button" variant="outline" className={BOUTON_PIED} onClick={onFermer} disabled={envoi}>
              Annuler
            </Button>
            <Button type="submit" className={`${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`} disabled={!propre || envoi}>
              {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
              Publier le commentaire
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const BlocProgramme = ({ programme, peutCommenter, onCommenter, onSupprimer }) => {
  const commence = programme.programme_id !== null && programme.programme_id !== undefined;
  const enRetard = estVrai(programme.en_retard);
  const chapitres = Array.isArray(programme.chapitres) ? programme.chapitres : [];
  const devoirs = Array.isArray(programme.devoirs) ? programme.devoirs : [];
  const commentaires = Array.isArray(programme.commentaires) ? programme.commentaires : [];
  const enCours = libelleDe(programme.element_en_cours) || programme.element_en_cours?.titre || '';
  const etat = !commence
    ? ['Programme non commencé', 'text-gray-500']
    : enRetard ? ['En retard', 'text-amber-700'] : ['À jour', 'text-emerald-700'];
  const titre = titreProgramme(programme);

  return (
    <section className={`rounded-lg border p-4 ${enRetard ? 'border-amber-200' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{titre}</h3>
        <p className={`text-xs font-semibold ${etat[1]}`}>
          {commence ? `${enPourcentage(programme.pourcentage)} · ` : ''}{etat[0]}
        </p>
      </div>

      {!commence ? (
        <p className="mt-2 text-[13px] text-gray-600">
          L'enseignant n'a pas encore commencé ce programme dans son espace.
        </p>
      ) : (
        <>
          <div className="mt-2">
            <BarreAvancement valeur={programme.pourcentage} libelle={`Avancement du programme ${titre}`} />
          </div>

          {chapitres.length === 0 ? (
            <p className="mt-3 text-[13px] text-gray-500">Aucun chapitre saisi.</p>
          ) : (
            <ol className="mt-3 grid gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
              {chapitres.map((ch, i) => (
                <li key={ch.id || `${i}-${ch.titre}`} className={`rounded-md border p-2 ${teinteChapitre(ch)}`}>
                  <p className="text-[11px] font-semibold">{i + 1}. {ch.titre}</p>
                  <p className="mt-0.5 text-[11px] text-gray-600">
                    {detailChapitre(ch)}{estVrai(ch.en_retard) && ch.statut !== 'termine' ? ' · en retard' : ''}
                  </p>
                </li>
              ))}
            </ol>
          )}

          {enCours && <p className="mt-2.5 text-xs text-gray-700">En cours : « {enCours} »</p>}
          <p className="mt-1 text-xs text-gray-500">
            Devoirs : {devoirs.length ? devoirs.map(phraseDevoir).join(' · ') : 'aucun déclaré.'}
          </p>
        </>
      )}

      {commentaires.length > 0 && (
        <ul className="mt-3 space-y-2">
          {commentaires.map((c) => (
            <li key={c.id} className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[11px] font-semibold text-[#246990]">
                  Commentaire de la direction{c.auteur ? ` · ${libelleDe(c.auteur)}` : ''}{c.cree_le ? ` · ${dateHeure(c.cree_le)}` : ''}
                </p>
                {estVrai(c.supprimable) && (
                  <button
                    type="button"
                    onClick={() => onSupprimer(c)}
                    aria-label={`Supprimer le commentaire du ${dateHeure(c.cree_le)}`}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 transition hover:bg-white hover:text-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-gray-700">{c.texte}</p>
            </li>
          ))}
        </ul>
      )}

      {peutCommenter && commence && (
        <Button size="sm" variant="outline" className="mt-3 gap-2 font-semibold" onClick={() => onCommenter(programme)}>
          <MessageSquarePlus className="h-4 w-4" />
          Commenter
        </Button>
      )}
    </section>
  );
};

/**
 * `onCommenter(programme, texte)` publie et rend la main (l'erreur remonte à
 * la fenêtre) ; `onSupprimer(commentaire)` supprime. La page relit ensuite.
 */
const ProgrammesEnseignant = ({ programmes, peutCommenter, onCommenter, onSupprimer }) => {
  const [pourCommentaire, setPourCommentaire] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppression, setSuppression] = useState(false);
  // Même verrou immédiat que la publication : un double clic sur « Supprimer »
  // enverrait deux retraits, le second refusé (404) en toast d'erreur.
  const suppressionEnCours = useRef(false);
  const liste = Array.isArray(programmes) ? programmes : [];

  const envoyer = async (programme, texte) => {
    await onCommenter(programme, texte);
    setPourCommentaire(null);
  };

  const confirmerSuppression = async () => {
    if (!aSupprimer || suppressionEnCours.current) return;
    suppressionEnCours.current = true;
    setSuppression(true);
    try {
      await onSupprimer(aSupprimer);
    } finally {
      suppressionEnCours.current = false;
      setSuppression(false);
      setASupprimer(null);
    }
  };

  if (liste.length === 0) {
    return (
      <EtatVide
        compact
        icone={BookOpen}
        titre="Aucune classe-matiere"
        description="Cet enseignant n'a aucune classe ni matiere affectee pour cette annee scolaire."
      />
    );
  }

  return (
    <div className="space-y-3.5">
      {liste.map((p, i) => (
        <BlocProgramme
          key={p.programme_id || `${libelleDe(p.classe)}-${libelleDe(p.matiere)}-${i}`}
          programme={p}
          peutCommenter={peutCommenter}
          onCommenter={setPourCommentaire}
          onSupprimer={setASupprimer}
        />
      ))}

      <DialogCommentaire programme={pourCommentaire} onFermer={() => setPourCommentaire(null)} onEnvoyer={envoyer} />

      <ConfirmDialog
        isOpen={Boolean(aSupprimer)}
        onClose={() => { if (!suppression) setASupprimer(null); }}
        onConfirm={confirmerSuppression}
        isLoading={suppression}
        type="warning"
        title="Supprimer ce commentaire"
        description="L'enseignant ne le verra plus sous son programme."
        confirmText="Supprimer"
        cancelText="Retour"
      />
    </div>
  );
};

export default ProgrammesEnseignant;
